/**
 * Learner-reported course issues — CLIENT-SIDE demo store.
 *
 * "Report an issue" on a course page saves here (localStorage on this device).
 * A report can be flagged for the learner's 1-to-1 instructor to go through in a
 * session; those flagged items surface in the course workspace. There is no
 * backend yet, so a manager cannot really read other learners' reports across
 * devices — the manager dashboard mixes these real on-device reports with a
 * demo team sample (see team-demo). Swap for a server store + email routing to
 * the content team once a backend is wired; the call-sites stay the same.
 */

export interface IssueReport {
  id: string;
  courseSlug: string;
  courseTitle: string;
  /** The page reported from: "Course overview" or a lesson title. */
  pageLabel: string;
  lessonTitle?: string;
  message: string;
  /** The learner wants their 1-to-1 instructor to go through this. */
  flagForInstructor: boolean;
  status: "open" | "addressed";
  createdAt: number;
}

const KEY = "tutorials:reports";

/** Fired after a report is added so open views (workspace, dashboard) refresh. */
export const REPORTS_EVENT = "tutorials:report-changed";

function read(): IssueReport[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]") as IssueReport[];
  } catch {
    return [];
  }
}

function write(list: IssueReport[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(REPORTS_EVENT));
  } catch {
    /* ignore */
  }
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `r-${Date.now().toString(36)}-${Math.round(performance.now())}`;
  }
}

export function addReport(
  input: Omit<IssueReport, "id" | "status" | "createdAt">,
): IssueReport {
  const report: IssueReport = {
    ...input,
    id: newId(),
    status: "open",
    createdAt: Date.now(),
  };
  const list = read();
  list.unshift(report);
  write(list);
  return report;
}

export function getReports(): IssueReport[] {
  return read();
}

/** Flagged-for-instructor reports, optionally for one course. */
export function getFlaggedReports(courseSlug?: string): IssueReport[] {
  return read().filter(
    (r) => r.flagForInstructor && (!courseSlug || r.courseSlug === courseSlug),
  );
}

export function markAddressed(id: string) {
  write(read().map((r) => (r.id === id ? { ...r, status: "addressed" } : r)));
}
